import { Typography } from 'antd'
import { useIndicationData } from 'features/indicationTriage/IndicationContext'
import { IndicationDetailCardProps } from 'features/indicationTriage/useIndicationDetailCards'
import { useRolodexEntries } from 'features/rolodex'
import React, { FC, ReactNode, useMemo, useState } from 'react'
import { RolodexEntryType } from 'types'

import { DAMAGE_MECHANISM_MAPPING } from './IndicationOverviewCard'

export interface ConcreteIndicationDetail {
  measurement_unit: string
  damage_mechanism: string
  description: string
  source: string
  size: string
  location: string
}

const INDICATION_OVERVIEW_ORDERING_TUPLE_LIST: [
  string,
  (detail?: ConcreteIndicationDetail) => ReactNode,
][] = [
  [
    'Type',
    (detail) => {
      const damageMechanism = detail?.damage_mechanism?.toUpperCase()
      if (damageMechanism && damageMechanism in DAMAGE_MECHANISM_MAPPING) {
        return DAMAGE_MECHANISM_MAPPING[damageMechanism]
      }
      return 'Unknown'
    },
  ],
  ['Size', (detail) => detail?.size?.trim() || 'Unknown'],
  [
    'Location',
    (detail) => {
      const location = detail?.location?.trim() || 'Unknown'
      return location !== 'Unknown'
        ? location
            .split(';')
            .map((loc, index) => <div key={index}>{loc.trim()}</div>)
        : 'Unknown'
    },
  ],
  [
    'Description',
    (detail) => (
      <DescriptionWithToggle
        description={detail?.description?.trim() || 'Unknown'}
      />
    ),
  ], // Use a new component for the description
]

const { Text } = Typography

interface OverviewConfig {
  show_ref_val_on_histogram: boolean
}

export const Title = () => 'Indication Overview'
export const Width = '24rem'

// Component to handle expanding and collapsing the description
const DescriptionWithToggle: FC<{ description: string }> = ({
  description,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const toggleDescription = () => setIsExpanded(!isExpanded)

  // Limit the number of characters to display when collapsed
  const maxLength = 100
  const shouldTruncate = description.length > maxLength

  return (
    <div>
      <div>
        {isExpanded || !shouldTruncate
          ? description // Show the full description if expanded or short
          : `${description.slice(0, maxLength)}...`}{' '}
        {/* Show truncated version */}
      </div>
      {shouldTruncate && (
        <Text
          type='secondary'
          onClick={toggleDescription}
          style={{ cursor: 'pointer', marginTop: '0.5rem', display: 'block' }}>
          {isExpanded ? 'Show Less' : 'Read More'}
        </Text>
      )}
    </div>
  )
}

export const CardView: FC<IndicationDetailCardProps<OverviewConfig>> = () => {
  const { indication: item } = useIndicationData()
  const { entries: indicationDetails } = useRolodexEntries(
    useMemo(
      () =>
        item
          ? [
              {
                type: RolodexEntryType.INDICATION,
                tags: {
                  indication: item.id,
                },
                limit: 1,
              },
            ]
          : [],
      [item]
    )
  ) as unknown as { entries: { data: { detail: ConcreteIndicationDetail } }[] }

  return (
    <div>
      {indicationDetails[0]?.data?.detail ? (
        <div>
          {INDICATION_OVERVIEW_ORDERING_TUPLE_LIST.map(([label, render]) => {
            const value = render(indicationDetails[0].data.detail)
            // Skip rendering if the value is null
            if (value === null) return null
            return (
              <div
                style={{
                  display: 'flex',
                  width: '100%',
                  flexDirection: 'row',
                  marginBottom: '1rem',
                }}
                key={label}>
                <Text strong style={{ minWidth: '44%' }}>
                  {label}
                </Text>
                <Text>{value}</Text> {/* Render the value directly */}
              </div>
            )
          })}
        </div>
      ) : (
        <div>Detail information is currently not available</div>
      )}
    </div>
  )
}
